import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { supabase } from '../../../lib/supabase';

interface NewsArticle {
  id: string;
  title: string;
  published: boolean;
}

interface BrandCount {
  brand: string;
  count: number;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [pricingCount, setPricingCount] = useState(0);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [brandCounts, setBrandCounts] = useState<BrandCount[]>([]);

  useEffect(() => {
    if (!sessionStorage.getItem('admin_auth')) navigate('/admin');
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      // Fetch pricing count and brand breakdown
      const { data: pricingData, error: pricingError } = await supabase
        .from('repair_pricing')
        .select('brand');

      if (!pricingError && pricingData) {
        setPricingCount(pricingData.length);
        const brandMap: Record<string, number> = {};
        pricingData.forEach((p) => { brandMap[p.brand] = (brandMap[p.brand] ?? 0) + 1; });
        setBrandCounts(Object.entries(brandMap).map(([brand, count]) => ({ brand, count })));
      }

      // Fetch news
      const { data: newsData, error: newsError } = await supabase
        .from('news_articles')
        .select('id, title, published')
        .order('published_at', { ascending: false });

      if (!newsError && newsData) {
        setNews(newsData);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const stats = [
    { icon: 'ri-price-tag-3-line', label: '維修項目總數', value: pricingCount, color: 'text-orange-500', bg: 'bg-orange-50' },
    { icon: 'ri-newspaper-line', label: '文章總數', value: news.length, color: 'text-blue-500', bg: 'bg-blue-50' },
    { icon: 'ri-eye-line', label: '已發布文章', value: news.filter((n) => n.published).length, color: 'text-green-500', bg: 'bg-green-50' },
    { icon: 'ri-draft-line', label: '草稿文章', value: news.filter((n) => !n.published).length, color: 'text-gray-500', bg: 'bg-gray-50' },
  ];

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">後台總覽</h1>
      <p className="text-gray-500 text-sm mb-8">歡迎回來，管理員</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className={`w-11 h-11 flex items-center justify-center ${s.bg} rounded-xl mb-4`}>
              <i className={`${s.icon} ${s.color} text-lg`} />
            </div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-gray-500 text-sm mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">維修報價管理</h2>
            <Link to="/admin/pricing" className="text-orange-500 text-sm hover:underline cursor-pointer">管理 →</Link>
          </div>
          <p className="text-gray-500 text-sm mb-4">目前共 {pricingCount} 項維修報價</p>
          <div className="space-y-2">
            {brandCounts.map(({ brand, count }) => (
              <div key={brand} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{brand}</span>
                <span className="text-gray-400">{count} 項</span>
              </div>
            ))}
          </div>
          <Link to="/admin/pricing" className="mt-4 w-full flex items-center justify-center py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-orange-600 transition-colors whitespace-nowrap">
            管理維修報價
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">最新消息管理</h2>
            <Link to="/admin/news" className="text-orange-500 text-sm hover:underline cursor-pointer">管理 →</Link>
          </div>
          <p className="text-gray-500 text-sm mb-4">共 {news.length} 篇，已發布 {news.filter(n => n.published).length} 篇</p>
          <div className="space-y-3">
            {news.slice(0, 3).map((n) => (
              <div key={n.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 line-clamp-1 flex-1 pr-2">{n.title}</span>
                <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${n.published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                  {n.published ? '已發布' : '草稿'}
                </span>
              </div>
            ))}
          </div>
          <Link to="/admin/news" className="mt-4 w-full flex items-center justify-center py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-gray-800 transition-colors whitespace-nowrap">
            管理最新消息
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-gray-900">頁面管理</h2>
              <p className="text-gray-500 text-sm mt-0.5">修改各頁面的橫幅圖片與文字內容</p>
            </div>
            <Link to="/admin/pages" className="text-orange-500 text-sm hover:underline cursor-pointer">前往管理 →</Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {['首頁 Hero 橫幅', '關於我們', 'iPhone 維修', 'iPad 維修', 'Mac 維修', '維修案例'].map(label => (
              <div key={label} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-image-line text-orange-400 text-xs" />
                </div>
                <span className="text-xs text-gray-600">{label}</span>
              </div>
            ))}
          </div>
          <Link to="/admin/pages" className="mt-4 w-full flex items-center justify-center py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl cursor-pointer transition-colors whitespace-nowrap">
            管理頁面內容
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}